import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  FiArrowRight, 
  FiChevronDown,
  FiRefreshCw,
  FiInfo,
  FiFileText,
  FiBook,
  FiMic,
  FiVideo,
  FiEye,
  FiPlay,
  FiTrendingUp,
  FiBarChart2,
  FiEdit,
  FiActivity,
  FiAward,
  FiCheckCircle,
  FiSearch,
  FiMessageCircle,
  FiMail,
  FiPhone,
  FiMapPin,
  FiInstagram,
  FiSend,
  FiMenu,
  FiX,
  FiUser
} from 'react-icons/fi';
import type { IconBaseProps, IconType } from 'react-icons';
import { FaInstagram, FaTelegramPlane } from 'react-icons/fa';
import { authAPI, UserProfile, homePageAPI, HomePageContentData } from '../services/api';
import { isAuthenticated as checkAuth } from '../config/api';
import { FALLBACK_HOME_PAGE_CONTENT } from '../constants/homePageDefaults';
import './LandingPage.css';
import NaatiLogo from './NaatiLogo';

const SERVICE_CARD_ICONS: IconType[][] = [
  [FiRefreshCw, FiInfo, FiFileText, FiBook],
  [FiMic, FiVideo, FiEye, FiPlay],
  [FiTrendingUp, FiBarChart2, FiEdit, FiActivity],
  [FiAward, FiCheckCircle, FiSearch, FiMessageCircle],
];

function normalizeHomePageContent(raw: HomePageContentData): HomePageContentData {
  const fb = FALLBACK_HOME_PAGE_CONTENT;
  const cardsIn = raw.service_cards?.length === 4 ? raw.service_cards : fb.service_cards;
  const service_cards = cardsIn.map((card, ci) => {
    const fbCard = fb.service_cards[ci] || fb.service_cards[0];
    const title = typeof card.title === 'string' && card.title.trim() ? card.title.trim() : fbCard.title;
    const items = [0, 1, 2, 3].map((ii) => {
      const v = card.items?.[ii];
      return typeof v === 'string' && v.trim() ? v.trim() : fbCard.items[ii];
    });
    return { title, items };
  });
  return { ...fb, ...raw, service_cards };
}

interface LandingPageProps {
  isAuthenticated?: boolean;
  userProfile?: UserProfile | null;
}

const LandingPage: React.FC<LandingPageProps> = ({ isAuthenticated: propIsAuthenticated, userProfile: propUserProfile }) => {
  const navigate = useNavigate();
  const [showCclDropdown, setShowCclDropdown] = useState(false);
  const [activeSection, setActiveSection] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(propIsAuthenticated || false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(propUserProfile || null);
  const [showHeroContent, setShowHeroContent] = useState(false);
  const landingPageRef = useRef<HTMLDivElement>(null);
  const [hp, setHp] = useState<HomePageContentData>(() => FALLBACK_HOME_PAGE_CONTENT);

  const sections = [
    { id: 'hero', name: 'Hero' },
    { id: 'material', name: 'Services' },
    { id: 'ccl-test', name: 'About' }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut" as const
      }
    }
  };

  const handleStartPractice = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      navigate('/login');
    }
  };

  // Show hero content after 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowHeroContent(true);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  // Check authentication status
  useEffect(() => {
    const checkAuthStatus = async () => {
      if (checkAuth()) {
        try {
          const profile = await authAPI.getProfile();
          setUserProfile(profile);
          setIsAuthenticated(true);
        } catch (error) {
          console.error('Failed to get user profile:', error);
          setIsAuthenticated(false);
          setUserProfile(null);
        }
      } else {
        setIsAuthenticated(false);
        setUserProfile(null);
      }
    };

    checkAuthStatus();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await homePageAPI.getPublic();
        if (!cancelled) setHp(normalizeHomePageContent(data));
      } catch {
        if (!cancelled) setHp(FALLBACK_HOME_PAGE_CONTENT);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (!landingPageRef.current) return;
      
      const scrollPosition = landingPageRef.current.scrollTop;
      const windowHeight = window.innerHeight;
      
      // Determine which section is currently in view
      const currentSection = Math.round(scrollPosition / windowHeight);
      setActiveSection(Math.min(currentSection, sections.length - 1));
    };

    const container = landingPageRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      handleScroll(); // Initial check
      
      return () => {
        container.removeEventListener('scroll', handleScroll);
      };
    }
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isMobileMenuOpen) {
        const target = event.target as HTMLElement;
        if (!target.closest('.mobile-menu') && !target.closest('.mobile-menu-toggle')) {
          setIsMobileMenuOpen(false);
        }
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Prevent body scroll when menu is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const scrollToSection = (index: number) => {
    if (!landingPageRef.current) return;
    const windowHeight = window.innerHeight;
    landingPageRef.current.scrollTo({
      top: index * windowHeight,
      behavior: 'smooth'
    });
  };

  return (
    <div className="landing-page" ref={landingPageRef}>
      {/* Navigation Bar */}
      <nav className={`landing-nav ${activeSection > 0 || isMobileMenuOpen ? 'landing-nav-with-bg' : ''} ${isMobileMenuOpen ? 'mobile-menu-open' : ''}`}>
        <div className="nav-container">
          <div className="nav-logo">
            <NaatiLogo className="landing-nav-logo-wrap" imgClassName="landing-logo-nav" size={80} />
          </div>
          
          {/* Desktop Navigation */}
          <div className="nav-links desktop-nav">
            {isAuthenticated ? (
              <button 
                className="nav-link"
                onClick={() => navigate(userProfile?.is_staff || userProfile?.is_superuser ? '/admin/dashboard' : '/dashboard')}
              >
                DASHBOARD
              </button>
            ) : (
              <>
                <button 
                  className="nav-link"
                  onClick={() => navigate('/')}
                >
                  HOME
                </button>
                <div 
                  className="nav-dropdown"
                  onMouseEnter={() => setShowCclDropdown(true)}
                  onMouseLeave={() => setShowCclDropdown(false)}
                >
                  <button className="nav-link nav-link-with-arrow">
                    NAATI
                    {React.createElement(FiChevronDown as React.ComponentType<{ size?: number; style?: React.CSSProperties }>, { 
                      size: 14, 
                      style: { marginLeft: '4px' } 
                    })}
                  </button>
                  <AnimatePresence>
                    {showCclDropdown && (
                      <motion.div
                        className="dropdown-menu"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                      >
                        <button className="dropdown-item">How to apply</button>
                        <button className="dropdown-item">Test Format</button>
                        <button className="dropdown-item">Test Marking</button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </>
            )}
            {isAuthenticated ? (
              <button 
                className="nav-link"
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                {React.createElement(FiUser as React.ComponentType<{ size?: number }>, { size: 16 })}
                {userProfile?.email || 'User'}
              </button>
            ) : (
              <>
                <button 
                  className="nav-link"
                  onClick={() => navigate('/signup')}
                >
                  SIGN UP
                </button>
                <button 
                  className="nav-link"
                  onClick={() => navigate('/login')}
                >
                  LOGIN
                </button>
              </>
            )}
          </div>

          {/* Mobile Hamburger Button */}
          <button 
            className="mobile-menu-toggle"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              React.createElement(FiX as React.ComponentType<{ size?: number }>, { size: 24 })
            ) : (
              React.createElement(FiMenu as React.ComponentType<{ size?: number }>, { size: 24 })
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              className="mobile-menu"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
            >
              <div className="mobile-menu-content">
                {isAuthenticated ? (
                  <>
                    <button 
                      className="mobile-nav-link"
                      onClick={() => {
                        navigate(userProfile?.is_staff || userProfile?.is_superuser ? '/admin/dashboard' : '/dashboard');
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      DASHBOARD
                    </button>
                    <button 
                      className="mobile-nav-link"
                      style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'space-between' }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {React.createElement(FiUser as React.ComponentType<{ size?: number }>, { size: 16 })}
                        {userProfile?.email || 'User'}
                      </span>
                    </button>
                  </>
                ) : (
                  <>
                    <button 
                      className="mobile-nav-link"
                      onClick={() => {
                        navigate('/');
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      HOME
                    </button>
                    
                    <div className="mobile-nav-dropdown">
                      <button 
                        className="mobile-nav-link mobile-nav-link-with-arrow"
                        onClick={() => setShowCclDropdown(!showCclDropdown)}
                      >
                        NAATI
                        {React.createElement(FiChevronDown as React.ComponentType<{ size?: number; style?: React.CSSProperties }>, { 
                          size: 16, 
                          style: { 
                            marginLeft: '8px',
                            transform: showCclDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.3s ease'
                          } 
                        })}
                      </button>
                      <AnimatePresence>
                        {showCclDropdown && (
                          <motion.div
                            className="mobile-dropdown-menu"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <button className="mobile-dropdown-item">How to apply</button>
                            <button className="mobile-dropdown-item">Test Format</button>
                            <button className="mobile-dropdown-item">Test Marking</button>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    <button 
                      className="mobile-nav-link"
                      onClick={() => {
                        navigate('/signup');
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      SIGN UP
                    </button>
                    <button 
                      className="mobile-nav-link"
                      onClick={() => {
                        navigate('/login');
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      LOGIN
                    </button>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Navigation Indicator - Left Side */}
      <div className="nav-indicator">
        {sections.map((section, index) => (
          <button
            key={section.id}
            className={`indicator-dot ${activeSection === index ? 'active' : ''}`}
            onClick={() => scrollToSection(index)}
            aria-label={`Go to ${section.name} section`}
          />
        ))}
      </div>

      {/* Social Media Icons - Right Side */}
      <div className="social-media-icons">
        <motion.a
          href={hp.instagram_url || '#'}
          target="_blank"
          rel="noopener noreferrer"
          className="social-icon"
          whileHover={{ scale: 1.2, y: -3 }}
          whileTap={{ scale: 0.95 }}
          aria-label="Follow us on Instagram"
        >
          {React.createElement(FaInstagram as React.ComponentType<{ size?: number }>, { size: 24 })}
        </motion.a>
        <motion.a
          href={hp.telegram_url || '#'}
          target="_blank"
          rel="noopener noreferrer"
          className="social-icon"
          whileHover={{ scale: 1.2, y: -3 }}
          whileTap={{ scale: 0.95 }}
          aria-label="Join us on Telegram"
        >
          {React.createElement(FaTelegramPlane as React.ComponentType<{ size?: number }>, { size: 24 })}
        </motion.a>
      </div>

      {/* Hero Section */}
      <section className="hero-section" id="hero">
        {/* Background Video */}
        <div className="hero-background">
          <video 
            className={`hero-bg-video ${showHeroContent ? 'hero-video-blurred' : 'hero-video-clean'}`}
            src="/images/video.mp4"
            autoPlay
            loop
            muted
            playsInline
          />
          <div className={`hero-background-overlay ${showHeroContent ? 'hero-overlay-visible' : 'hero-overlay-hidden'}`}></div>
        </div>
        
        <div className="hero-container">
          <AnimatePresence>
            {showHeroContent && (
              <motion.div
                className="hero-content"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                exit="hidden"
              >
                <motion.h1 
                  className="hero-title"
                  variants={itemVariants}
                >
                  {hp.hero_title}
                </motion.h1>

                <motion.p 
                  className="hero-subtitle"
                  variants={itemVariants}
                >
                  {hp.hero_subtitle}
                </motion.p>

                <motion.div
                  className="hero-cta"
                  variants={itemVariants}
                >
                  <motion.button
                    className="cta-button"
                    onClick={handleStartPractice}
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {hp.cta_button_label}
                    {React.createElement(FiArrowRight as React.ComponentType<{ size?: number }>, { size: 20 })}
                  </motion.button>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Our Services Section - Page 2 */}
      <section className="material-section" id="material">
        <div className="material-container">
          <motion.div
            className="material-content"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8 }}
          >
            <motion.h2 
              className="material-title"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {hp.services_section_title}
            </motion.h2>

            <motion.div 
              className="material-divider"
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
            />

            {/* Feature Cards */}
            <motion.div 
              className="material-cards"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              {hp.service_cards.map((card, ci) => {
                const icons = SERVICE_CARD_ICONS[ci] || SERVICE_CARD_ICONS[0];
                const cardClass = `material-card card-${ci + 1}`;
                return (
                  <motion.div
                    key={ci}
                    className={cardClass}
                    whileHover={{ y: -8, scale: 1.02 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h3 className="card-title">{card.title}</h3>
                    <ul className="card-items">
                      {card.items.map((text, ii) => {
                        const IconComp = (icons[ii] || FiInfo) as React.ComponentType<IconBaseProps>;
                        return (
                          <li key={ii}>
                            <IconComp size={18} />
                            <span>{text}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </motion.div>
                );
              })}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* What is NAATI Test Section + Footer - Page 3 */}
      <section className="ccl-test-section" id="ccl-test">
        <div className="ccl-test-container">
          <motion.div
            className="ccl-test-content"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8 }}
          >
            <motion.h2 
              className="ccl-test-title"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {hp.about_title}
            </motion.h2>

            <motion.div 
              className="ccl-test-divider"
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
            />

            <motion.p 
              className="ccl-test-description"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              {hp.about_body}
            </motion.p>
          </motion.div>
        </div>

        {/* Footer inside NAATI Test Section */}
        <footer className="footer-section">
        <div className="footer-container">
          <div className="footer-content">
            {/* Column 1: Logo & Description */}
            <motion.div 
              className="footer-column"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="footer-logo">
                <NaatiLogo imgClassName="landing-logo-footer" size={96} />
              </div>
              <p className="footer-description">
                {hp.footer_tagline}
              </p>
            </motion.div>

            {/* Column 2: Quick Links */}
            <motion.div 
              className="footer-column"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <h4 className="footer-title">Quick Links</h4>
              <ul className="footer-links">
                <li><a href="#hero">Home</a></li>
                <li><a href="/login">Login</a></li>
                <li><a href="/signup">Sign Up</a></li>
              </ul>
            </motion.div>

            {/* Column 3: Contact Info */}
            <motion.div 
              className="footer-column"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h4 className="footer-title">Contact Us</h4>
              <ul className="footer-contact">
                <li>
                  {React.createElement(FiMail as React.ComponentType<{ size?: number }>, { size: 18 })}
                  <span>{hp.contact_email}</span>
                </li>
                <li>
                  {React.createElement(FiPhone as React.ComponentType<{ size?: number }>, { size: 18 })}
                  <span>{hp.contact_phone}</span>
                </li>
                <li>
                  {React.createElement(FiMapPin as React.ComponentType<{ size?: number }>, { size: 18 })}
                  <span>{hp.contact_address}</span>
                </li>
              </ul>
            </motion.div>

            {/* Column 4: Social Media */}
            <motion.div 
              className="footer-column"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <h4 className="footer-title">Follow Us</h4>
              <div className="footer-social">
                <a
                  href={hp.instagram_url || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-social-link"
                >
                  {React.createElement(FiInstagram as React.ComponentType<{ size?: number }>, { size: 24 })}
                </a>
                <a
                  href={hp.telegram_url || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-social-link"
                >
                  {React.createElement(FiSend as React.ComponentType<{ size?: number }>, { size: 24 })}
                </a>
              </div>
              <p className="footer-copyright">
                {hp.footer_copyright}
              </p>
            </motion.div>
          </div>
        </div>
      </footer>
      </section>
    </div>
  );
};

export default LandingPage;

